import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import { cqCompanySettingsQuery, saveCqCompanySettings, type CqCompanySettings } from '../../api/connectionQuality'
import { toast } from '../../ui/notify'
import { WifiDiagHelp } from '../../devices/WifiDiagHelp'
import { SPEED_HELP, SpeedHourSelect } from '../../devices/CqSpeedDaily'
import '../download_agent/DownloadAgentPage.css'
import '../connection_quality/ConnectionQualitySettingsPage.css'

/**
 * Implicitul de Connection Quality al firmei ("Default Connection Quality"),
 * coloana din stanga a paginii Settings -> Connection Quality. Asezat ca
 * Agent Install: trei pasi numerotati, fiecare cu explicatia langa campul
 * lui, si Save sub ei - nu un tabel cu textele de ajutor gramada la final.
 *
 * Fiecare aparat il mosteneste pana cand cineva alege altceva pe cardul lui
 * din Agent Settings -> Functions. Oprit cat timp nu l-a aprins nimeni.
 *
 * Faza 3: "Allow Wi-Fi diagnostics" - implicitul firmei pentru bifa care lasa
 * agentul sa permita locatia pe masina (BSSID-ul AP-ului pe Windows 11).
 *
 * Faza 4: "Daily speed test" si ora lui - implicitul firmei, mostenit de
 * fiecare aparat ca restul.
 */
export function ConnectionQualityPanel() {
  const { data, error } = useQuery(cqCompanySettingsQuery)
  if (error || !data) return null
  return (
    <Panel
      key={`${data.enabled}:${data.target ?? ''}:${data.interval_s}:${data.wifi_diag ? 1 : 0}:${data.speed_auto ? 1 : 0}:${data.speed_hour ?? 3}`}
      page={data}
    />
  )
}

function Panel({ page }: { page: CqCompanySettings }) {
  const query = useQueryClient()
  const [enabled, setEnabled] = useState(page.enabled)
  const [target, setTarget] = useState(page.target ?? '')
  const [interval, setInterval] = useState(String(page.interval_s))
  const [wifiDiag, setWifiDiag] = useState(page.wifi_diag ?? false)
  const [speedAuto, setSpeedAuto] = useState(page.speed_auto ?? false)
  const [speedHour, setSpeedHour] = useState(page.speed_hour ?? 3)
  const [problem, setProblem] = useState('')
  const [busy, setBusy] = useState(false)
  const locked = !page.canWrite || busy

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setProblem('')
    saveCqCompanySettings({
      enabled,
      target: target.trim(),
      interval_s: Number(interval),
      wifi_diag: wifiDiag,
      speed_auto: speedAuto,
      speed_hour: speedHour,
    })
      .then(async (next) => {
        query.setQueryData(cqCompanySettingsQuery.queryKey, next)
        await query.invalidateQueries({ queryKey: ['device'] })
        toast('Settings saved', 'Connection Quality')
      })
      .catch((e: unknown) => {
        const message = e instanceof ApiError && e.message ? e.message : 'Something went wrong.'
        setProblem(message)
        toast(message, 'Connection Quality', 'error')
      })
      .finally(() => setBusy(false))
  }

  return (
    <section data-panel="connection-quality">
      <div className="od-cq-section">
        <h3>Default Connection Quality</h3>
        <p>
          The company default. Every device inherits it until someone chooses otherwise on the device&apos;s Agent Settings
          &rarr; Functions tab.
        </p>
      </div>
      <form id="cq-company-settings" onSubmit={submit}>
        <Step
          number={1}
          title="Measure the connection"
          lead="Pings the router, the internet provider and the target; times DNS, TCP, TLS and the first byte; counts TCP retransmissions."
        >
          <div className="od-da-card">
            <div className="od-cq-check" style={{ marginBottom: 12 }}>
              <input type="checkbox" id="cqEnabled" checked={enabled} disabled={locked} onChange={(e) => setEnabled(e.target.checked)} />
              <label htmlFor="cqEnabled">Measure the connection</label>
              {page.enabled ? (
                <span className="od-da-pill od-da-pill-ok" data-state="on">
                  <i className="fas fa-check-circle" aria-hidden="true"></i> On for the company
                </span>
              ) : (
                <span className="od-da-pill od-da-pill-wait" data-state="off">
                  Off for the company
                </span>
              )}
            </div>
            <div className="od-cq-field">
              <label htmlFor="cqTarget">Target</label>
              <div>
                <input
                  type="text"
                  className="form-control"
                  id="cqTarget"
                  placeholder={page.defaultTarget}
                  value={target}
                  disabled={locked}
                  onChange={(e) => setTarget(e.target.value)}
                />
                <p className="od-cq-help">A host name or IP address. Leave it empty for {page.defaultTarget}.</p>
              </div>
            </div>
            <div className="od-cq-field">
              <label htmlFor="cqInterval">Every (seconds)</label>
              <div>
                <input
                  type="number"
                  className="form-control"
                  id="cqInterval"
                  min={page.limits.min}
                  max={page.limits.max}
                  value={interval}
                  disabled={locked}
                  onChange={(e) => setInterval(e.target.value)}
                />
                <p className="od-cq-help">
                  How often the device measures, from {page.limits.min} to {page.limits.max} seconds.
                </p>
              </div>
            </div>
          </div>
        </Step>

        <Step number={2} title="Wi-Fi diagnostics" lead="Lets the agent see which access point each Windows 11 machine is connected to.">
          <div className="od-da-card">
            <div className="od-cq-check">
              <input type="checkbox" id="cqWifiDiag" checked={wifiDiag} disabled={locked} onChange={(e) => setWifiDiag(e.target.checked)} />
              <label htmlFor="cqWifiDiag">Allow Wi-Fi diagnostics</label>
            </div>
            <p className="od-cq-help" data-help="wifi-diag">
              <WifiDiagHelp doc={page.locationDoc} />
            </p>
          </div>
        </Step>

        <Step number={3} title="Daily speed test" lead="Measures download and upload speed once a day, at the hour you choose.">
          <div className="od-da-card">
            <div className="od-cq-check">
              <input type="checkbox" id="cqSpeedAuto" checked={speedAuto} disabled={locked} onChange={(e) => setSpeedAuto(e.target.checked)} />
              <label htmlFor="cqSpeedAuto">Daily speed test</label>
            </div>
            <p className="od-cq-help" data-help="speed-daily">
              {page.speedHelp ?? SPEED_HELP}
            </p>
            <div className="od-cq-field" style={{ marginTop: 12 }}>
              <label htmlFor="cqSpeedHour">Speed test at (device time)</label>
              <div>
                <SpeedHourSelect id="cqSpeedHour" value={speedHour} disabled={locked || !speedAuto} onChange={setSpeedHour} />
                <p className="od-cq-help">The device&apos;s own clock: each machine tests at this hour in its own time zone.</p>
              </div>
            </div>
          </div>
        </Step>

        {problem ? (
          <p className="od-cq-problem text-danger" role="alert">
            {problem}
          </p>
        ) : null}
        {page.canWrite ? (
          <div className="od-cq-save">
            <button className="btn btn-success" type="submit" disabled={busy}>
              <i className="far fa-save" aria-hidden="true"></i>Save
            </button>
            <p>Saved for the whole company. Devices with their own setting keep it.</p>
          </div>
        ) : (
          <p className="od-cq-help" style={{ marginLeft: 48 }}>
            You can see these settings, but only someone with the edit right can change them.
          </p>
        )}
      </form>
    </section>
  )
}

function Step({ number, title, lead, children }: { number: number; title: string; lead: ReactNode; children: ReactNode }) {
  return (
    <div className="od-da-step">
      <div className="od-da-num">{number}</div>
      <div className="od-da-body">
        <h3>{title}</h3>
        <p className="od-da-lead">{lead}</p>
        {children}
      </div>
    </div>
  )
}
